import React, {
  Dispatch,
  FC,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import closedEye from "assets/icons/closedEye.svg";
import openedEye from "assets/icons/openedEye.svg";
import classnames from "classnames";
import {
  Controller,
  type FieldError,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ErrorMessage } from "@hookform/error-message";
import { Autocomplete, FormGroup, TextField } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";

import CatalogItemActionsComponent from "../../features/Catalogs/CatalogItemActionsComponent";
import ConfirmationModal from "../../features/ConfirmationModal";
import FormButtons from "../../features/FormButtons";
import { useAppDispatch, useAppSelector, useSetServerError } from "../../hooks";
import { countries } from "../../models/countries";
import { IUser, IUserPostArgs, IUserPutArgs } from "../../models/user";
import {
  changePassword,
  changeUserEmail,
  clearCatalogTextFieldErrors,
  editUser,
  getUsers,
} from "../../store";
import { autocompleteStyles } from "../../styles/MUIStyles";
import { StyledSelect, StyledSelectError } from "../../ui/Select";
import { sendNotification } from "../../ui/Toast";

import styles from "views/Catalogs/CatalogsLayout.module.scss";

const UpdateUserForm: FC<{
  createOrUpdate: string | null;
  setIsDrawerOpen: Dispatch<SetStateAction<boolean>>;
  isDrawerOpen: boolean;
  activeUser: IUser | null;
  setActiveUser: (activeUser: IUser | null) => void;
  setCreateOrUpdate: Dispatch<SetStateAction<string | null>>;
  debouncedSearchUserName: string;
  debouncedSearchUserEmail: string;
  pageNumber: number;
  pageSize: number;
  userStatus: string;
  sortConfig: { column: string | null; direction: string | null };
}> = ({
  createOrUpdate,
  setIsDrawerOpen,
  isDrawerOpen,
  activeUser,
  setActiveUser,
  setCreateOrUpdate,
  debouncedSearchUserName,
  debouncedSearchUserEmail,
  userStatus,
  pageNumber,
  pageSize,
  sortConfig,
}): JSX.Element => {
  const { t } = useTranslation();
  const currentLanguage = localStorage.getItem("i18nextLng");

  const dispatch = useAppDispatch();

  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);

  const countries = useAppSelector((state) => state.catalogs.countries);
  const userRoles = useAppSelector((state) => state.users.roles);
  const userErrors = useAppSelector((state) => state.users.userErrors);
  const isLoading = useAppSelector((state) => state.users.isLoading);
  const isUserEmailChangingLoading = useAppSelector(
    (state) => state.users.isUserEmailChangingLoading,
  );

  const userStatuses = ["active", "inactive", "markedForDeletion"];

  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isPasswordVisibleConfirm, setIsPasswordVisibleConfirm] =
    useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    setError,
    formState: { errors, isSubmitted  },
    clearErrors,
    control,
    watch,
    reset,
  } = useForm<IUserPutArgs>({
    mode: "onSubmit",
  });

  const hasPasswordError = !!errors?.password;
  const hasConfirmPasswordError = !!errors?.confirmPassword;

  const onSubmit: SubmitHandler<any> = async (formData: any) => {
    try {
      // 1. POST changeemailuser
      if (activeUser && formData.email !== activeUser.email) {
        const emailResponse = await dispatch(
          changeUserEmail({
            id: activeUser.id,
            userEmail: formData.email,
          }),
        );

        if (emailResponse.meta.requestStatus !== "fulfilled") {
          sendNotification(t("errors.somethingWrongWithEmail"), "error");
          return;
        }
      }

      // 2. PUT users
      const editResponse = await dispatch(
        editUser({
          id: activeUser?.id!,
          firstName: formData.firstName,
          middleName: formData.middleName,
          lastName: formData.lastName,
          roleNames: formData.roleNames,
          phoneNumber: formData.phoneNumber,
          userStatus: formData.userStatus,
          countryId: formData.countryId,
        }),
      );

      if (editResponse.meta.requestStatus !== "fulfilled") {
        sendNotification(t("errors.somethingWrongWithEdit"), "error");
        return;
      }

      // 3. POST changepassword
      if (formData.password) {
        const passwordResponse = await dispatch(
          changePassword({
            userId: activeUser?.id!,
            password: formData.password,
            confirmPassword: formData.confirmPassword,
          }),
        );

        if (passwordResponse.meta.requestStatus !== "fulfilled") {
          sendNotification(t("errors.somethingWrongWithPassword"), "error");
          return;
        }
      }
      sendNotification(t("notifications.successfullySaved"), "success");

      await dispatch(
        getUsers({
          userName: debouncedSearchUserName,
          email: debouncedSearchUserEmail,
          pageNumber,
          userStatus,
          pageSize,
          sortBy: sortConfig,
        }),
      );

      setIsDrawerOpen(false);
      setActiveUser(null);
    } catch (error) {
      console.error("Error in onSubmit:", error);
      sendNotification(t("errors.unexpectedError"), "error");
    }
  };

  const setFormData = (fields: IUser | null) => {
    // Object.entries(fields).forEach(([key, value]) => form.setValue(key, value)
    setValue("email", fields?.email || "");
    setValue("firstName", fields?.firstName || "");
    setValue("middleName", fields?.middleName || "");
    setValue("lastName", fields?.lastName || "");
    setValue("phoneNumber", fields?.phoneNumber || "");
    setValue("userStatus", fields?.userStatus || "");
    setValue("countryId", fields?.countryId || "");
    setValue("password", "");
    setValue("confirmPassword", "");
  };

  const formClose = () => {
    if (formChangesChecking()) setConfirmationModalOpen(true);
    else closeFormDrawer();
  };

  const closeFormDrawer = () => {
    setCreateOrUpdate(null);
    setIsDrawerOpen(false);
  };

  const closeModalConfirm = () => {
    setFormData(null);
    setIsDrawerOpen(false);
    setConfirmationModalOpen(false);
  };

  // returns true, if form was updated
  const formChangesChecking = () => {
    return Boolean(
      getValues("firstName") !== activeUser?.firstName ||
        getValues("middleName") !== activeUser?.middleName ||
        getValues("lastName") !== activeUser?.lastName ||
        getValues("phoneNumber") !== activeUser?.phoneNumber ||
        getValues("userStatus") !== activeUser?.userStatus ||
        getValues("countryId") !== activeUser?.countryId,
    );
  };

  useEffect(() => {
    if (isDrawerOpen && activeUser) {
      reset(
        {
          email: activeUser.email,
          firstName: activeUser.firstName,
          middleName: activeUser.middleName,
          lastName: activeUser.lastName,
          phoneNumber: activeUser.phoneNumber,
          userStatus: activeUser.userStatus,
          countryId: activeUser.countryId,
          password: "",
          confirmPassword: "",
          roleNames: activeUser.roleNames,
        },
        {
          keepErrors: false,
          keepDirty: false,
          keepTouched: false,
          keepIsSubmitted: false,
        },
      );
    } else {
      reset();
      dispatch(clearCatalogTextFieldErrors());
    }
  }, [isDrawerOpen, activeUser, dispatch]);

  useSetServerError({
    serverError: userErrors,
    setError,
    clearErrors,
    errorNames: [
      "email",
      "firstName",
      "middleName",
      "lastName",
      "roleName",
      "phoneNumber",
      "userStatus",
      "countryId",
      "password",
    ],
  });

  // const getClassNames = (error: FieldError | undefined): string => {
  //   return error ? styles.catalogsDrawerInputError : styles.catalogsDrawerInput;
  // };

  const getClassNames = (
  error: FieldError | undefined,
  isSubmitted: boolean,
): string => {
  return error && isSubmitted
    ? styles.catalogsDrawerInputError
    : styles.catalogsDrawerInput;
};

const filterOptions = (options: any, { inputValue }: any) => {
      const lang = currentLanguage === "en-US" ? "en" : "ru";
      const normalizedInput = inputValue.toLowerCase();
      return [...options]
        .filter((opt) => {
          const label = opt.name;
          return label.toLowerCase().includes(normalizedInput);
        })
        .sort((a, b) => {
          const aStarts = a.name.toLowerCase().startsWith(normalizedInput);
          const bStarts = b.name.toLowerCase().startsWith(normalizedInput);

          if (aStarts && !bStarts) return -1;
          if (!aStarts && bStarts) return 1;

          return a.name.localeCompare(b.name, lang, {
            sensitivity: "base",
          });
        });
    };

  return (
    <>
      <CatalogItemActionsComponent
        name="users"
        isDrawerOpen={isDrawerOpen}
        createOrUpdate={createOrUpdate}
        closeFormDrawer={formClose}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.formInputWrapper}>
            <label htmlFor="email" className={styles.drawerTopSubTitle}>
              {t("data.email")}
            </label>
            <div>
              <input
                {...register("email", {
                  required: t("validation.requiredField") as string,
                  maxLength: {
                    value: 256,
                    message: t("validation.maxLength256"),
                  },
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]+$/i,
                    message: t("validation.invalidEmail") as string,
                  },
                })}
                id="email"
                type="text"
                autoComplete="off"
                placeholder={t("data.email") as string}
                className={getClassNames(errors.email, isSubmitted)}
              />
              <ErrorMessage
                name="email"
                errors={errors}
                render={({ message }) => <span>{message}</span>}
              />
            </div>
            <label htmlFor="firstName" className={styles.drawerTopSubTitle}>
              {t("data.firstName")}
            </label>
            <div>
              <input
                {...register("firstName", {
                  required: t("validation.requiredField") as string,
                  maxLength: {
                    value: 50,
                    message: t("validation.maxLength50"),
                  },
                  pattern: {
                    value: /^[А-ЯЁA-Z\s-]+$/i,
                    message: t("validation.onlyLetters") as string,
                  },
                })}
                id="firstName"
                type="text"
                autoComplete="off"
                placeholder={t("data.firstName") as string}
                className={getClassNames(errors.firstName, isSubmitted)}
              />
              <ErrorMessage
                name="firstName"
                errors={errors}
                render={({ message }) => <span>{message}</span>}
              />
            </div>
            <label htmlFor="middleName" className={styles.drawerTopSubTitle}>
              {t("data.middleName")}
            </label>
            <div>
              <input
                {...register("middleName", {
                  maxLength: {
                    value: 50,
                    message: t("validation.maxLength50"),
                  },
                  pattern: {
                    value: /^[А-ЯЁA-Z\s-]+$/i,
                    message: t("validation.onlyLetters") as string,
                  },
                })}
                id="middleName"
                type="text"
                autoComplete="off"
                placeholder={t("data.middleName") as string}
                className={getClassNames(errors.middleName, isSubmitted)}
              />
              <ErrorMessage
                name="middleName"
                errors={errors}
                render={({ message }) => <span>{message}</span>}
              />
            </div>
            <label htmlFor="lastName" className={styles.drawerTopSubTitle}>
              {t("data.lastName")}
            </label>
            <div>
              <input
                {...register("lastName", {
                  required: t("validation.requiredField") as string,
                  maxLength: {
                    value: 50,
                    message: t("validation.maxLength50"),
                  },
                  pattern: {
                    value: /^[А-ЯЁA-Z\s-]+$/i,
                    message: t("validation.onlyLetters") as string,
                  },
                })}
                id="lastName"
                type="text"
                autoComplete="off"
                placeholder={t("data.lastName") as string}
                className={getClassNames(errors.lastName, isSubmitted)}
              />
              <ErrorMessage
                name="lastName"
                errors={errors}
                render={({ message }) => <span>{message}</span>}
              />
            </div>
            <label htmlFor="password" className={styles.drawerTopSubTitle}>
              {t("data.password")}
            </label>
            <div className={styles.loginFormInnerPasswordContainer}>
              <input
                id="password"
                type={isPasswordVisible ? "text" : "password"}
                autoComplete="new-password"
                placeholder={t("data.password") as string}
                className={getClassNames(errors.password, isSubmitted)}
                {...register("password", {
                  validate: (value) => {
                    if (!value) return true;

                    const validations = [
                      {
                        test: value.length >= 8,
                        message: t("validation.minLength8"),
                      },
                      {
                        test: /[A-Z]/.test(value),
                        message: t("validation.mustIncludeUpperCase"),
                      },
                      {
                        test: /[!@#$%^&*(),.?":{}|<>]/.test(value),
                        message: t("validation.mustIncludeSpecialChar"),
                      },
                      {
                        test: /\d/.test(value),
                        message: t("validation.mustIncludeDigit"),
                      },
                    ];

                    const invalid = validations.find((v) => !v.test);
                    return invalid ? invalid.message : true;
                  },
                })}
              />
              {isPasswordVisible ? (
                <img
                  className={classnames(styles.loginFormInnerPasswordEye, {
                    [styles.loginFormInnerPasswordEyeError]: hasPasswordError,
                  })}
                  src={openedEye}
                  onClick={() =>
                    setIsPasswordVisible((prevState) => !prevState)
                  }
                  alt="password eye"
                />
              ) : (
                <img
                  className={classnames(styles.loginFormInnerPasswordEye, {
                    [styles.loginFormInnerPasswordEyeError]: hasPasswordError,
                  })}
                  src={closedEye}
                  onClick={() =>
                    setIsPasswordVisible((prevState) => !prevState)
                  }
                  alt="password eye"
                />
              )}
              {/* <ErrorMessage
                name="password"
                errors={errors}
                render={({ message }) => <span>{message}</span>}
              /> */}
              {isSubmitted && (
  <ErrorMessage
    name="password"
    errors={errors}
    render={({ message }) => <span>{message}</span>}
  />
)}
            </div>
            <label
              htmlFor="confirmPassword"
              className={styles.drawerTopSubTitle}
            >
              {t("data.confirmPassword")}
            </label>
            <div className={styles.loginFormInnerPasswordContainer}>
              <input
                {...register("confirmPassword", {
                  // validate: (value) =>
                  //   value === watch("password") ||
                  //   (t("validation.passwordsDoNotMatch") as string),
                  validate: (value) => {
                    return value === getValues("password")
                      ? true
                      : (t("validation.passwordsDoNotMatch") as string);
                  },
                })}
                id="confirmPassword"
                type={isPasswordVisibleConfirm ? "text" : "password"}
                autoComplete="off"
                placeholder={t("data.confirmPassword") as string}
                className={getClassNames(errors.confirmPassword, isSubmitted)}
              />
              {isPasswordVisibleConfirm ? (
                <img
                  className={classnames(styles.loginFormInnerPasswordEye, {
                    [styles.loginFormInnerPasswordEyeError]:
                      hasConfirmPasswordError,
                  })}
                  src={openedEye}
                  onClick={() =>
                    setIsPasswordVisibleConfirm((prevState) => !prevState)
                  }
                  alt="password eye"
                />
              ) : (
                <img
                  className={classnames(styles.loginFormInnerPasswordEye, {
                    [styles.loginFormInnerPasswordEyeError]:
                      hasConfirmPasswordError,
                  })}
                  src={closedEye}
                  onClick={() =>
                    setIsPasswordVisibleConfirm((prevState) => !prevState)
                  }
                  alt="password eye"
                />
              )}
              <ErrorMessage
                name="confirmPassword"
                errors={errors}
                render={({ message }) => <span>{message}</span>}
              />
            </div>
            <FormControl fullWidth sx={{ marginBottom: "1rem" }}>
              <span className={styles.drawerTopSubTitle}>
                {t("data.roleName")}
              </span>
              <Controller
                name="roleNames"
                control={control}
                defaultValue={activeUser?.roleNames || []}
                rules={{
                  required: t("validation.requiredField") as string,
                }}
                render={({ field }) => (
                  <FormGroup>
                    {userRoles.map((role) => (
                      <label key={role.roleName}>
                        <input
                          type="checkbox"
                          value={role.roleName}
                          checked={field.value.includes(role.roleName)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              field.onChange([...field.value, role.roleName]);
                            } else {
                              field.onChange(
                                field.value.filter((r) => r !== role.roleName),
                              );
                            }
                          }}
                        />
                        {t(`roles.${role.roleName}`)}
                      </label>
                    ))}
                  </FormGroup>
                )}
              />
              <ErrorMessage
                name="roleNames"
                errors={errors}
                render={({ message }) => (
                  <span className={styles.selectErrorMsg}>{message}</span>
                )}
              />
            </FormControl>
            <label htmlFor="phoneNumber" className={styles.drawerTopSubTitle}>
              {t("data.phoneNumber")}
            </label>
            <div>
              <input
                {...register("phoneNumber", {
                  maxLength: {
                    value: 50,
                    message: t("validation.maxLength50"),
                  },
                })}
                id="phoneNumber"
                type="text"
                autoComplete="off"
                placeholder={t("data.phoneNumber") as string}
                className={getClassNames(errors.phoneNumber, isSubmitted)}
              />
              <ErrorMessage
                name="phoneNumber"
                errors={errors}
                render={({ message }) => <span>{message}</span>}
              />
            </div>
            <FormControl fullWidth sx={{ marginBottom: "1rem" }}>
              <span className={styles.drawerTopSubTitle}>
                {t("data.status")}
              </span>
              <Controller
                name="userStatus"
                defaultValue=""
                control={control}
                rules={{
                  required:
                    createOrUpdate === "create"
                      ? (t("validation.requiredField") as string)
                      : false,
                }}
                render={({ field }) => (
                  <Select
                    {...field}
                    input={
                      errors?.userStatus ? (
                        <StyledSelectError />
                      ) : (
                        <StyledSelect />
                      )
                    }
                  >
                    {userStatuses.map((item, index) => (
                      <MenuItem key={item} value={index + 1}>
                        {t(`filters.${item}`)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              <ErrorMessage
                name="userStatus"
                errors={errors}
                render={({ message }) => (
                  <span className={styles.selectErrorMsg}>{message}</span>
                )}
              />
            </FormControl>
            <FormControl fullWidth sx={{ marginBottom: "1rem" }}>
              <span className={styles.drawerTopSubTitle}>
                {t("data.country")}
              </span>
              <Controller
                name="countryId"
                defaultValue=""
                control={control}
                rules={{
                  required:
                    createOrUpdate === "update"
                      ? (t("validation.requiredField") as string)
                      : false,
                }}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    {...field}
                    ListboxProps={{
                      style: { maxHeight: "200px" },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        sx={autocompleteStyles}
                        placeholder={t("data.country") || ""}
                        error={!!fieldState.error}
                      />
                    )}
                    options={[...countries].sort(
                      (a: countries, b: countries) => {
                        return a.name.localeCompare(
                          b.name,
                          currentLanguage === "en-US" ? "en" : "ru",
                        );
                      },
                    )}
                    filterOptions={filterOptions}
                    getOptionLabel={(option) => option.name}
                    onChange={(event, value: countries | null) =>
                      field.onChange(value?.id)
                    }
                    value={
                      countries.filter(
                        (el) => el?.id === getValues("countryId"),
                      )[0] || null
                    }
                  />
                )}
              />
              <ErrorMessage
                name="countryId"
                errors={errors}
                render={({ message }) => (
                  <span className={styles.selectErrorMsg}>{message}</span>
                )}
              />
            </FormControl>
          </div>
          <FormButtons
            isLoading={isLoading || isUserEmailChangingLoading}
            createOrUpdate={createOrUpdate}
            cancelHandler={() => formClose()}
          />
        </form>
      </CatalogItemActionsComponent>
      <ConfirmationModal
        isModalOpen={isConfirmationModalOpen}
        setIsModalOpen={setConfirmationModalOpen}
        actionName={"Close"}
        instanceName=""
        message={"closeFormWithoutSaving"}
        handleAction={() => closeModalConfirm()}
      />
    </>
  );
};

export default UpdateUserForm;
